/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState } from "react";
import {
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Box,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useNavigate, useLocation } from "react-router-dom";

const GlobalAdminSidebar = () => {
  const [openDomainManagement, setOpenDomainManagement] = useState(true);
  const [selectedPortfolio, setSelectedPortfolio] = useState("ZEEKR");
  const navigate = useNavigate();
  const location = useLocation();

  const handleDomainManagementClick = () => {
    setOpenDomainManagement(!openDomainManagement);
  };

  const handleNavigate = (path, section) => {
    navigate(path, { state: { selectedSection: section } });
  };

  const handlePortfolioChange = (event) => {
    setSelectedPortfolio(event.target.value);
  };

  // Determine if we're on the categories page and which section is active
  const isOnCategoriesPage = location.pathname.includes(
    "/harman-admin/categories"
  );
  const isOnFlexMetadataPage = location.pathname.includes(
    "/harman-admin/flex-metadata"
  );
  const selectedSection = location.state?.selectedSection || "Categories";

  return (
    <Box
      sx={{
        width: "280px",
        height: "calc(100vh - 64px)",
        bgcolor: "#FFFFFF",
        position: "fixed",
        top: "64px",
        left: "48px",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Portfolio Dropdown - Hidden for future use */}
      <Box sx={{ padding: "16px 16px 8px 16px", display: "none" }}>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: "#717273",
            textTransform: "uppercase",
            marginBottom: "8px",
            letterSpacing: "0.5px",
          }}
        >
          PORTFOLIO
        </Typography>
        <Select
          value={selectedPortfolio}
          onChange={handlePortfolioChange}
          fullWidth
          size="small"
          sx={{
            fontSize: "14px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E0E0E0",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#BDBDBD",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#0476E2",
            },
          }}
        >
          <MenuItem value="ZEEKR">ZEEKR</MenuItem>
          <MenuItem value="Portfolio 2">Portfolio 2</MenuItem>
          <MenuItem value="Portfolio 3">Portfolio 3</MenuItem>
        </Select>
      </Box>

      {/* Navigation Menu */}
      <List component="nav" sx={{ padding: "8px 12px", flex: 1 }}>
        {/* Flex Metadata */}

        <ListItemButton 
          sx={{
            padding: "10px 12px",
            borderRadius: "4px",
            marginBottom: "4px",
            minHeight: "36px",
            "&:hover": {
              backgroundColor: "#E5F3FF",
            },
            "&.Mui-selected": {
              backgroundColor: "#E5F3FF",
              "&:hover": {
                backgroundColor: "#E5F3FF",
              },
            },
          }}
          selected={isOnFlexMetadataPage}
          onClick={() =>
            handleNavigate(
              "/ui/harman-admin/flex-metadata",
              "GlobalAdminFlexMetadata"
            )
          }
        >
          {/* spacer to align with arrow icon in Domain Management */}
          <Box sx={{ width: "20px", marginRight: "4px" }} />
          <ListItemText
            primary="Flex Metadata"
            slotProps={{
              primary: {
                fontSize: "14px",
                fontWeight: 400,
                color: isOnFlexMetadataPage ? "#0476E2" : "#2A2C2D",
              },
            }}
          />
        </ListItemButton>

        {/* Domain Management Section */}
        <ListItemButton
          onClick={handleDomainManagementClick}
          sx={{
            padding: "10px 12px",
            borderRadius: "4px",
            marginBottom: "4px",
            minHeight: "36px",
            "&:hover": {
              backgroundColor: "#F5F5F5",
            },
          }}
        >
          {openDomainManagement ? (
            <ArrowDropDownIcon
              sx={{ color: "#717273", fontSize: "20px", marginRight: "4px" }}
            />
          ) : (
            <ArrowRightIcon
              sx={{ color: "#717273", fontSize: "20px", marginRight: "4px" }}
            />
          )}
          <ListItemText
            primary="Domain Management"
            slotProps={{
              primary: {
                fontSize: "14px",
                fontWeight: 400,
                color: "#2A2C2D",
              },
            }}
          />
        </ListItemButton>

        {/* Collapsible Sub-items */}
        <Collapse in={openDomainManagement} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {/* Categories */}
            <ListItemButton
              onClick={() =>
                handleNavigate("/ui/harman-admin/categories", "Categories")
              }
              selected={isOnCategoriesPage && selectedSection === "Categories"}
              sx={{
                pl: "52px",
                padding: "8px 12px 8px 52px",
                borderRadius: "4px",
                marginBottom: "2px",
                minHeight: "32px",
                "&:hover": {
                  backgroundColor: "#E5F3FF",
                },
                "&.Mui-selected": {
                  backgroundColor: "#E5F3FF",
                  "&:hover": {
                    backgroundColor: "#E5F3FF",
                  },
                },
              }}
            >
              <ListItemText
                primary="Categories"
                slotProps={{
                  primary: {
                    fontSize: "14px",
                    fontWeight: 400,
                    color:
                      isOnCategoriesPage && selectedSection === "Categories"
                        ? "#0476E2"
                        : "#717273",
                  },
                }}
              />
            </ListItemButton>

            {/* Make Model Year */}
            <ListItemButton
              onClick={() =>
                handleNavigate("/ui/harman-admin/categories", "MakeMyModel")
              }
              selected={isOnCategoriesPage && selectedSection === "MakeMyModel"}
              sx={{
                pl: "52px",
                padding: "8px 12px 8px 52px",
                borderRadius: "4px",
                marginBottom: "2px",
                minHeight: "32px",
                "&:hover": {
                  backgroundColor: "#E5F3FF",
                },
                "&.Mui-selected": {
                  backgroundColor: "#E5F3FF",
                  "&:hover": {
                    backgroundColor: "#E5F3FF",
                  },
                },
              }}
            >
              <ListItemText
                primary="Make Model year"
                slotProps={{
                  primary: {
                    fontSize: "14px",
                    fontWeight: 400,
                    color:
                      isOnCategoriesPage && selectedSection === "MakeMyModel"
                        ? "#0476E2"
                        : "#717273",
                  },
                }}
              />
            </ListItemButton>
          </List>
        </Collapse>
      </List>
    </Box>
  );
};

export default GlobalAdminSidebar;
